import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize isAuthenticated based on the token's presence
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(sessionStorage.getItem("accessToken")));

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "accessToken" && event.oldValue !== event.newValue) {
        setIsAuthenticated(Boolean(event.newValue)); // Update based on the new value
      }
    };

    // Listen to session storage changes
    window.addEventListener("storage", handleStorageChange);

    // Clean up the event listener when the component unmounts
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const login = async (accessToken, refreshToken, expiresIn) => {
    const expirationTimestamp = Date.now() + expiresIn * 1000; // Convert expiresIn to milliseconds and add to current time
    sessionStorage.setItem("accessToken", accessToken);
    sessionStorage.setItem("refreshToken", refreshToken);
    sessionStorage.setItem("accessTokenExpiry", expirationTimestamp.toString());

    setIsAuthenticated(true);
    return Promise.resolve(); // Ensure the function returns a promise that resolves after state is set
  };

  // SEC-009: revoke refresh server-side (security stamp), then clear local session.
  const logout = async () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");

    try {
      await fetch("/api/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refreshToken: refreshToken || "" }),
      });
    } catch {
      // Always clear local session even if the revoke call fails.
    }

    sessionStorage.clear();
    setIsAuthenticated(false);
  };

  return <AuthContext.Provider value={{ isAuthenticated, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
